import {Component, Inject, OnInit} from '@angular/core';
import {DatePipe} from '@angular/common';
import {MAT_DIALOG_DATA, MatDialog} from '@angular/material/dialog';
import {Router} from '@angular/router';

import {ClipboardService} from 'ngx-clipboard';

import {DeclineReasonComponent} from './decline-reason/decline-reason.component';
import {DataService, MessagesService} from 'src/app/services';
import {interval} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {Recipient} from "../../../models";

@Component({
    selector: 'app-monitor-info',
    templateUrl: './monitor-info.component.html',
    styleUrls: ['./monitor-info.component.scss']
})
export class MonitorInfoComponent implements OnInit {
    alertId: any;
    alertDetails: any;
    messagePreview: any = {message: ''};
    alertSelected = false;
    alertStatuses: any[];
    dateFormat = 'd MMM y, h:mm:ss a';
    recipientSelected = false;
    selectedRecipient: any;
    currentUserInfo: any;
    currentUser: any;
    isAdmin: boolean;
    loaded = false;
    statusColumns = [
        {name: 'Status', sortable: false, width: 350},
        {
            name: 'Received',
            sortable: false,
            pipe: {transform: (timestamp) => this.datePipe.transform(timestamp, this.dateFormat)},
            width: 100
        }
    ];
    statusSort = [
        {prop: 'received', dir: 'desc'}
    ];
    private pauseRefresh: boolean = false;
    private expandAll: boolean = false;

    constructor(public datePipe: DatePipe,
                private router: Router,
                private dataService: DataService,
                public messagesService: MessagesService,
                private _clipboardService: ClipboardService,
                private dialog: MatDialog,
                @Inject(MAT_DIALOG_DATA) public alert: any) {
        this.alertId = this.alert.currentAlert.id;
        this.alert = this.alert.currentAlert;
    }

    ngOnInit() {
        this.dataService.getUserInfo().subscribe(res => {
            this.currentUserInfo = res;
            this.isAdmin = this.currentUserInfo.isAdmin;
            this.dataService.getIndividualAndAssociateExactMatchLookup(this.currentUserInfo['email'])
            .subscribe({
                next: resi => this.currentUser = resi.content[0] || null,
                error: () => this.currentUser = null
            }).add(() => this.loaded = true);
        });
        const refreshMinuteInterval = interval(1000 * 60);
        refreshMinuteInterval.pipe(takeUntil(this.router.events)).subscribe(() => this.selectAlert(this.alertId));
        this.selectAlert(this.alertId);
    }

    copy(text: string) {
        this._clipboardService.copyFromContent(text);
    }

    public sentChat(recipient) {
        return recipient.address_type === 'CHAT';
    }

    public sentSMS(recipient) {
        return (recipient.address_type === 'SMS' && /[+0-9]/.test(recipient.address));
    }

    public sentEMail(recipient) {
        return (recipient.address_type === 'EMAIL' &&
            /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                .test(recipient.address.toLowerCase()));
    }

    public sentTeams(recipient) {
        return recipient.address_type === 'TEAMS';
    }


    getAlertMessage(alertId: number) {
        this.messagesService.getMessagePreview(alertId).subscribe((res: any) => this.messagePreview = res);
    }

    expand(id) {
        return id === 0 || this.expandAll;
    }

    selectAlert(alertId) {
        if(!this.pauseRefresh) {
            this.getAlertDetails(alertId);
            this.getAlertMessage(alertId);
            this.alertSelected = true;
            this.recipientSelected = false;
        }
    }

    getAlertDetails(alertId) {
        this.messagesService.getAlertDetailsData(alertId).subscribe((res: any) => {
            this.alertDetails = res;
            // Select First Recipient by default
            const firstRecipient = this.alertDetails.escalations.recipients ? this.alertDetails.escalations.recipients[0] : this.getFirstRecipient(this.alertDetails.escalations);
            if (firstRecipient) {
                this.onSelectRecipient(null, firstRecipient.id);
                this.getCountPerGroup();
            }
        });
    }

    getFirstRecipient(array) {
        if (array) {
            if (array.groups) {
                if (array.groups[0].recipients) {
                    return array.groups[0].recipients[0];
                } else if (array.groups[0].groups) {
                    return this.getFirstRecipient(array.groups[0].groups);
                }
            } else if (array.recipients) {
                return array.recipients[0];
            } else if (array['INIT']) {
                return this.getFirstRecipient(array['INIT']);
            } else {
                return this.getFirstRecipient(array[0]);
            }
        }
    }

    isUnknownRecipientsGroup(groupName) {
        return groupName === 'Unknown Recipient(s)';
    }

    isRecipientAddressNull(recipientAddress) {
        return recipientAddress === '*****null';
    }

    onSelectRecipient(event, recipientId) {
        this.alertDetails.escalations.forEach(escalation => {
            escalation.groups.forEach(group => {
                if (group.groups) {
                    group.groups.forEach(subGroup => {
                        subGroup.recipients.forEach(recipient => {
                            if (recipientId === recipient.id) {
                                this.selectedRecipient = recipient;
                            }
                        });
                    });
                } else {
                    group.recipients.forEach(recipient => {
                        if (recipientId === recipient.id) {
                            this.selectedRecipient = recipient;
                        }
                    });
                }
            });
        });
        this.alertStatuses = this.selectedRecipient.statuses;
        this.recipientSelected = true;
    }

    getRecipientName(recipient, ignoreGroup) {
        return (ignoreGroup ? undefined : recipient.group) || recipient.individual || recipient.channel_name || recipient.address;
    }

    replyBack(alertId, buttonValue) {
        this.router.navigateByUrl(`/sendMessage?id=${alertId}&value=${buttonValue}`, {skipLocationChange: true});
    }

    hasAccepted(recipient): boolean {
        for (const status of recipient.statuses) {
            if (status.status.includes('ACCEPT')) {
                return true;
            } else if (status.status.includes('DECLINE')) {
                return false;
            }
        }
        return false;
    }

    hasDeclined(recipient): boolean {
        for (const status of recipient.statuses) {
            if (status.status.includes('ACCEPT')) {
                return false;
            } else if (status.status.includes('DECLINE')) {
                return true;
            }
        }
        return false;
    }

    isError(recipient) {
        return (this.isRecipientAddressNull(recipient.address) || this.isWebhookError(recipient) || this.isTwilioError(recipient));
    }

    isWebhookError(recipient) {
        for (const status of recipient.statuses) {
            if (status.status.includes('Error sending webhook message')) {
                return true;
            }
        }
        return false;
    }

    isTwilioError(recipient) {
        for (const status of recipient.statuses) {
            if (/failed|undelivered/ig.test(status.status)) {
                return true;
            }
        }
        return false;
    }

    isSender(): boolean {
        if(this.alert.from !== undefined) {
            return this.currentUser.associate_email.toLowerCase() === this.alert.from.toLowerCase();
        }
        return false;
    }

    acceptButtonCheck(recipient: any): boolean {
        if (!recipient.ack_required) return false;
        if (this.hasAccepted(recipient)) return false;
        return this.isAdmin || this.currentUser.id === recipient.individual_id || this.isSender();
    }

    declineButtonCheck(recipient: any): boolean {
        if (!recipient.ack_required) return false;
        if (this.hasDeclined(recipient)) return false;
        return this.isAdmin || this.currentUser.id === recipient.individual_id || this.isSender();
    }

    accept(recipient: any) {
        this.messagesService.acknowledge(recipient.provider_reference, 'accept').subscribe( (res: any) => {
            this.loaded = false;
            location.reload();
        });
    }

    decline(recipient: any) {
        const popupDialog = this.dialog.open(DeclineReasonComponent, {
            disableClose: true,
            width: '85%'
        });
        popupDialog.afterClosed().subscribe(result => {
            if (result) {
                this.messagesService.acknowledge(recipient.provider_reference, 'decline', result).subscribe((res: any) => {
                    this.loaded = false;
                    location.reload();
                });
            }
        });
    }

    getCountPerGroup() {
        for (const escalation of this.alertDetails.escalations) {
            escalation.acceptedCount = 0;
            escalation.declinedCount = 0;
            escalation.recipientCount = 0;
            for (let i = 0; i < escalation.groups.length; i++) {
                escalation.groups[i] = this.getGroupCount(escalation.groups[i]);
                escalation.acceptedCount += escalation.groups[i].acceptedCount;
                escalation.declinedCount += escalation.groups[i].declinedCount;
                escalation.recipientCount += escalation.groups[i].recipientCount;
            }
        }
    }

    getGroupCount(group: any) {
        group.acceptedCount = 0;
        group.declinedCount = 0;
        group.recipientCount = 0;
        if (group.recipients !== undefined) {
            for (const recipient of group.recipients) {
                if (recipient.ack_required) {
                    if (this.hasAccepted(recipient)) {
                        group.acceptedCount++;
                    } else if (this.hasDeclined(recipient)) {
                        group.declinedCount++;
                    }
                    group.recipientCount++;
                }
            }
        }
        if (group.groups !== undefined) {
            const newGroups = [];
            for (const subgroup of group.groups) {
                const newGroup = this.getGroupCount(subgroup);
                group.acceptedCount += newGroup.acceptedCount;
                group.declinedCount += newGroup.declinedCount;
                group.recipientCount += newGroup.recipientCount;
                newGroups.push(newGroup);
            }
            group.groups = newGroups;
        }
        return group;
    }

    isRequired(group): boolean {
        for (const recipient of group.recipients) {
            if (recipient.ack_required) return true;
        }
        return false;
    }

    getRecipientEntityId(recipient: Recipient) : string {
        return   recipient.device_id      ? "channel-"    + recipient.device_id
               : recipient.individual_id  ? "individual-" + recipient.individual_id
               : recipient.group_id       ? "group-"      + recipient.group_id
               : recipient.super_group_id ? "supergroup-" + recipient.super_group_id
               : "phone-" + recipient.address;
    }
}
